import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminCreateCoupon, adminListCoupons, adminUpdateCoupon } from "../lib/api";
import { couponLabel } from "../lib/coupons";

export default function CouponsTab({ adminKey }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", type: "flat", value: 50, max_discount: "", first_order_only: false, description: "", start_date: "", end_date: "", weekends_only: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListCoupons(adminKey);
      setCoupons(r.coupons || []);
    } catch (e) {
      toast.error("Could not load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Coupon code required"); return; }
    setCreating(true);
    try {
      await adminCreateCoupon(adminKey, {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value) || 0,
        max_discount: form.type === "percent" && form.max_discount ? Number(form.max_discount) : null,
        first_order_only: form.first_order_only,
        description: form.description,
        start_date: form.start_date,
        end_date: form.end_date,
        weekends_only: form.weekends_only,
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm({ code: "", type: "flat", value: 50, max_discount: "", first_order_only: false, description: "", start_date: "", end_date: "", weekends_only: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (code, active) => {
    try {
      await adminUpdateCoupon(adminKey, code, { active });
      toast.success(`${code} ${active ? "activated" : "disabled"}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="admin-coupons-panel">
      <form
        onSubmit={create}
        className="lg:col-span-1 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 space-y-3 h-fit"
        data-testid="coupon-create-form"
      >
        <div className="font-display text-xl">Create coupon</div>
        <label className="block">
          <span className="overline">Code</span>
          <input
            className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm font-mono uppercase"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
            placeholder="FIRST50"
            data-testid="coupon-input-code"
          />
        </label>
        <div className="flex gap-2" data-testid="coupon-type-toggle">
          {[["flat", "Flat ₹"], ["percent", "Percent %"]].map(([t, l]) => (
            <button
              type="button"
              key={t}
              onClick={() => setForm({ ...form, type: t })}
              className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${form.type === t ? "bg-rw-ink text-white" : "border border-rw-ink/20"}`}
              data-testid={`coupon-type-${t}`}
            >
              {l}
            </button>
          ))}
        </div>
        {form.type === "percent" && (
          <label className="block">
            <span className="overline">Max discount cap ₹ (optional)</span>
            <input
              type="number"
              min="1"
              className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
              value={form.max_discount}
              onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
              placeholder="No cap"
              data-testid="coupon-input-max-discount"
            />
          </label>
        )}
        <label className="block">
          <span className="overline">{form.type === "percent" ? "Discount (%)" : "Flat discount (₹)"}</span>
          <input
            type="number"
            min="1"
            max={form.type === "percent" ? 100 : undefined}
            className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            data-testid="coupon-input-value"
          />
        </label>
        <label className="block">
          <span className="overline">Description (optional)</span>
          <input
            className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="₹50 off first order"
            data-testid="coupon-input-desc"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          {["start_date", "end_date"].map((k) => (
            <label key={k} className="block">
              <span className="overline">{k === "start_date" ? "Starts" : "Ends"} (optional)</span>
              <input
                type="date"
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-2 py-2 text-sm"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`coupon-input-${k.replace("_", "-")}`}
              />
            </label>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.weekends_only}
            onChange={(e) => setForm({ ...form, weekends_only: e.target.checked })}
            data-testid="coupon-input-weekends-only"
          />
          Weekends only (Sat & Sun)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.first_order_only}
            onChange={(e) => setForm({ ...form, first_order_only: e.target.checked })}
            data-testid="coupon-input-first-order"
          />
          First order only (per phone)
        </label>
        <button
          type="submit"
          disabled={creating}
          className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
          data-testid="coupon-create-btn"
        >
          {creating ? "Creating…" : "Create coupon"}
        </button>
        <p className="text-xs text-rw-ink3">
          Coupons only show on the site and work at checkout inside their date window (IST). Leave dates blank for always-on.
        </p>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {loading ? (
          <div className="py-10 text-center text-rw-ink2">Loading…</div>
        ) : coupons.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
            No coupons yet. FIRST50 will appear here after backend seed runs.
          </div>
        ) : (
          coupons.map((c) => (
            <div
              key={c.code}
              className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`coupon-row-${c.code}`}
            >
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono font-semibold text-lg">{c.code}</span>
                  <span className="font-display text-xl" data-testid={`coupon-value-${c.code}`}>{couponLabel(c)}</span>
                </div>
                <div className="text-xs text-rw-ink2 mt-1">
                  {c.description || (c.type === "percent" ? "Percent discount" : "Flat discount")}
                  {c.first_order_only && <span className="ml-2 px-2 py-0.5 rounded-full bg-rw-mint text-[10px] uppercase tracking-widest">first order only</span>}
                  {c.weekends_only && <span className="ml-2 px-2 py-0.5 rounded-full bg-rw-mint text-[10px] uppercase tracking-widest">weekends only</span>}
                </div>
                <div className="text-xs text-rw-ink3 mt-1" data-testid={`coupon-window-${c.code}`}>
                  {c.start_date || c.end_date ? `${c.start_date || "…"} → ${c.end_date || "…"}` : "No date limit"}
                  <span className={`ml-2 font-semibold ${c.live ? "text-green-700" : "text-rw-ink3"}`} data-testid={`coupon-live-${c.code}`}>
                    {c.live ? "● Live now" : "○ Not live"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => toggle(c.code, !c.active)}
                className={`text-xs rounded-full px-3 py-1.5 font-semibold ${c.active ? "bg-rw-mint" : "border border-rw-ink/20 text-rw-ink3"}`}
                data-testid={`coupon-toggle-${c.code}`}
              >
                {c.active ? "Active" : "Disabled"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
